



































































import Glide, { Breakpoints, Controls } from '@glidejs/glide/dist/glide.modular.esm';
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount, onMounted,
  onUpdated, PropType,
  ref,
  toRefs,
  watch,
} from '@nuxtjs/composition-api';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';

import { SimpleImage } from '~/components/common/SimpleImage';
import { arrowThumbnailsDisabler as ArrowThumbnailsDisabler } from '~/helpers/arrowThumbnailsDisabler';
import isServer from '~/helpers/isServer';

import { CarouselOptions } from '../types';

const DEFAULT_BORDER_WIDTH = 99;

export default defineComponent({
  name: 'Thumbnails',
  components: {
    SimpleImage,
  },
  props: {
    options: {
      type: Object as PropType<CarouselOptions>,
      default: () => ({
        type: 'slider',
        autoplay: false,
        rewind: false,
        perView: 3.2,
        gap: 0,
        dragThreshold: 0,
        bound: true,
        breakpoints: {
          600: { perView: 5 },
          1024: { perView: 4 },
          1920: { perView: 3.2 },
        },
      }),
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
    showNavigation: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array as PropType<object[]>,
      required: true,
    },
    isZoom: {
      type: Boolean,
      default: false,
    },
    hideImageAtIndex: {
      type: Number,
      default: null,
    },
  },
  emits: ['loaded', 'goAction'],
  setup(props, { emit }) {
    const {
      options, activeIndex, isZoom,
    } = toRefs(props);

    let carousel = null;

    const thumbnailsRef = ref(null);
    const thumbPositionX = ref<string>('0');
    const borderWidth = ref<string>('90px');

    const sliderOptions = computed(() => ({ ...options.value, startAt: 0 }));
    const classSuffix = computed(() => (isZoom.value ? 'Zoom' : ''));

    const updateThumbPositionX = (index: number) => {
      const slide = thumbnailsRef.querySelector ? thumbnailsRef.querySelector('.glide__slide') : null;
      const slideWidth = slide?.clientWidth || borderWidth.value?.replace('px', '') || 0;
      thumbPositionX.value = index * slideWidth < 0 ? '0px' : `${index * slideWidth}px`;
    };

    const setBorderWidth = () => {
      if (isServer) {
        borderWidth.value = `${DEFAULT_BORDER_WIDTH}px`;
        return;
      }

      borderWidth.value = `${thumbnailsRef.value?.querySelector('.glide__slide')?.clientWidth || DEFAULT_BORDER_WIDTH}px`;
    };
    let resizeTimeout;
    const goTo = (index: number) => {
      const nextIndex = index ?? activeIndex.value;
      const perView = window.innerWidth < 600 ? 5 : (window.innerWidth < 1024 ? 4 : 3);

      if (thumbnailsRef.value && carousel) {
        const currentPage = +carousel.index;

        const currentPageEnd = currentPage + perView - 1;

        if ((nextIndex < currentPage || nextIndex > currentPageEnd) && !props.isZoom) {
          let newPage = nextIndex < currentPage ? nextIndex : nextIndex - perView + 1;
          newPage = Math.max(0, newPage);
          carousel.go(`=${newPage}`);
        }

        updateThumbPositionX(nextIndex);
      }

      emit('goAction', +nextIndex);
    };

    watch(activeIndex, goTo);

    onMounted(() => {
      emit('loaded');

      nextTick(() => {
        if (isZoom.value) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          disableBodyScroll(thumbnailsRef.value);
        }

        if (props.images.length === 0) {
          return;
        }

        if (thumbnailsRef.value) {
          const thumbsGlide = new Glide(thumbnailsRef.value, sliderOptions.value);
          thumbsGlide.mount({ Controls, Breakpoints, ArrowThumbnailsDisabler });
          carousel = thumbsGlide;
          setBorderWidth();
          updateThumbPositionX(activeIndex.value);

          thumbsGlide.on('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              setBorderWidth();
              updateThumbPositionX(activeIndex.value);
            }, 1000);
          });
        }
      });
    });

    onUpdated(() => {
      if (carousel) {
        nextTick(() => {
          carousel.mount({ Controls, Breakpoints, ArrowThumbnailsDisabler });
        });
      }
    });

    onBeforeUnmount(() => {
      carousel?.destroy();
      clearAllBodyScrollLocks();
    });

    return {
      borderWidth,
      thumbPositionX,
      thumbnailsRef,
      classSuffix,
      goTo,
    };
  },
});
